import type { FC, PropsWithChildren } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import { type LocationFilterDTO } from '@cognite/apm-config';

import {
  AppConfigContext,
  type AppConfigContextValue,
} from './app-config-context';
import { CdmAppConfigProviderContext } from './cdm-app-config-provider.context';
import { type InfieldIdmConfig } from './types';
import { convertInfieldLocationConfigToAppConfig } from './utils/convert-infield-location-config-to-app-config';
import { convertInfieldLocationConfigToLocation } from './utils/convert-infield-location-config-to-location';
import { convertInfieldLocationConfigsToIdmLocations } from './utils/convert-infield-location-configs-to-idm-locations';

export const CdmAppConfigProvider: FC<PropsWithChildren> = ({ children }) => {
  const { useCdmSelectedLocationContext, registerInfieldModeInMetrics } =
    useContext(CdmAppConfigProviderContext);

  const { selectedLocationConfig, allLocationConfigs } =
    useCdmSelectedLocationContext();

  useEffect(() => {
    registerInfieldModeInMetrics('cdm');
  }, [registerInfieldModeInMetrics]);

  const appConfigContext: AppConfigContextValue = useMemo(() => {
    if (!selectedLocationConfig) {
      throw new Error(
        'CdmAppConfigProvider: selectedLocationConfig is missing. Please contact support.'
      );
    }
    if (allLocationConfigs.length === 0) {
      throw new Error(
        'CdmAppConfigProvider: allLocationConfigs is empty. Please contact support.'
      );
    }
    const appConfig = convertInfieldLocationConfigToAppConfig(
      selectedLocationConfig,
      allLocationConfigs
    );
    const idmLocations: LocationFilterDTO[] =
      convertInfieldLocationConfigsToIdmLocations(allLocationConfigs);

    const location: InfieldIdmConfig = convertInfieldLocationConfigToLocation(
      selectedLocationConfig
    );

    return {
      appConfig, // App config used to be shared for all locations before cdm
      isIdm: true,
      idmLocations, // All available CDM location configurations, converted to the legacy IDM location filter format.
      location, // The selected CDM location configuration, converted to the legacy InfieldIdmConfig format.
    };
  }, [selectedLocationConfig, allLocationConfigs]);

  return (
    <AppConfigContext.Provider value={appConfigContext}>
      {children}
    </AppConfigContext.Provider>
  );
};
